// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the orrery as three.js objects, shared by the browser's Solar
 * System view (ui/solarSystemView.ts) and the headset (vrSolarSystem.ts).
 * Plan: docs/AYNI_XR_SOLAR_SYSTEM_PLAN.md.
 *
 * Two ways to be drawn:
 * - **Overview**: the bird's-eye orrery, laid out by `layout.ts` (true
 *   distances with Pluto's orbit 5 m across, or true sizes), in metres in
 *   the `root` group, which the host places (the browser at the origin,
 *   the headset where the globe stood). Bodies smaller than a marker on
 *   the eye are drawn as markers of fixed size with a label; the orbits
 *   as lines.
 * - **Flight**: true distances and true sizes at the arriving body's
 *   scale (it ends as large as the globe it replaces), every position
 *   worked out in float64 from the viewer each frame, so nothing jitters
 *   at millions of km. `root` then stands at the viewer, turned so the
 *   flight runs straight ahead. Objects farther than {@link FAR_M} are
 *   drawn at that distance, shrunk in proportion (same size on the eye).
 *
 * Frames: the ephemeris works in the J2000 ecliptic; `root` holds it as
 * three's axes, x → x, ecliptic north → +y, y → −z.
 */

import type * as THREE from 'three'
import { createVrPlanetRings, type VrPlanetRingsHandle } from '../vrPlanetRings'
import { SOLAR_BODIES, type SolarBody, type SolarBodyId } from './bodies'
import { AU_KM, clampEphemerisTime, equatorialToEcliptic, heliocentricAu, moonGeocentricKm, OBLIQUITY_J2000, orbitPath, type Vec3 } from './ephemeris'
import { remainingKm, streakStrength } from './flight'
import { layoutBodyPoint, layoutPoint, layoutRadius, SIZES_MOON_GAP_M, type OrreryLayout } from './layout'

/** In a flight, anything farther is drawn at this distance and shrunk to match, metres (inside the 40 m sky). */
export const FAR_M = 30
/** A marker's diameter on the eye, CSS pixels (planets; dwarfs and the Moon a little less; the Sun more). */
const MARKER_PX: Record<SolarBody['kind'], number> = { star: 18, planet: 9, dwarf: 7, moon: 7 }
const LABEL_PX = 15
/** The layout switch takes this long, seconds. */
const LAYOUT_SECONDS = 1.2
const ORBIT_POINTS = 256
const STREAKS = 520

export interface SolarSceneOptions {
  /** The body's name in the reader's language. */
  nameOf: (id: SolarBodyId) => string
  /** The picture that dresses a body's sphere, or null for its plain colour. */
  pictureUrl?: (id: SolarBodyId) => string | null
}

export interface SolarFlightStart {
  from: SolarBodyId
  to: SolarBodyId
  timeMs: number
  /** The viewer, world. */
  viewer: THREE.Vector3
  /** Straight ahead (unit, world): where the arriving body ends up. */
  ahead: THREE.Vector3
  /** The viewer's up (world). */
  up: THREE.Vector3
  /** From the viewer to where the arriving body's centre ends, metres. */
  arrivalDistanceM: number
  /** The arriving body's radius on arrival, metres (the globe's). */
  arrivalRadiusM: number
}

export interface SolarSceneHandle {
  readonly root: THREE.Group
  readonly layout: OrreryLayout
  readonly timeMs: number
  readonly flying: boolean
  setLayout(layout: OrreryLayout, animate?: boolean): void
  setTime(ms: number): void
  /** The overview's place in the world: the host's choice (it is restored after a flight). */
  setPlacement(position: THREE.Vector3, quaternion: THREE.Quaternion, scale: number): void
  /** Starts a flight: `root` moves to the viewer. Returns the trip's true distance, km. */
  beginFlight(start: SolarFlightStart): number
  /** Where the flight is, 0..1. */
  setFlightProgress(u: number): void
  /** Back to the overview placement. */
  endFlight(): void
  /** Per frame: markers and labels to their size on the eye, the layout switch, the streaks. */
  update(cameraWorld: THREE.Vector3, pixelsPerRadian: number, dtSeconds: number): void
  /** The body a ray points at (within `toleranceRad`, or its own disc), nearest the ray first. */
  pick(origin: THREE.Vector3, dir: THREE.Vector3, toleranceRad: number): SolarBodyId | null
  setHover(id: SolarBodyId | null): void
  /** A body's centre, world. */
  bodyWorldPosition(id: SolarBodyId, out: THREE.Vector3): THREE.Vector3
  /** A body's radius as drawn, world metres. */
  bodyWorldRadius(id: SolarBodyId): number
  /** Celestial (J2000 equatorial) → world, row-major 3×3, for the night sky. */
  celestialToWorld(): number[]
  /** Names in a new language. */
  relabel(): void
  dispose(): void
}

interface BodyNode {
  body: SolarBody
  group: THREE.Group
  sphere: THREE.Mesh
  uniforms: { uMap: { value: THREE.Texture | null }; uHasMap: { value: number }; uColour: { value: THREE.Color }; uSun: { value: THREE.Vector3 } } | null
  glow: THREE.Sprite | null
  marker: THREE.Sprite
  label: THREE.Sprite
  labelAspect: number
  orbit: THREE.LineLoop | null
  rings: VrPlanetRingsHandle | null
  /** Radius in the root's units as drawn now. */
  radius: number
  pictureAsked: boolean
}

const toThree = (v: Vec3): Vec3 => [v[0], v[2], -v[1]]

const BODY_VERT = /* glsl */ `
varying vec3 vNormalW;
varying vec3 vPosW;
varying vec2 vUv;
void main() {
  vUv = uv;
  vec4 w = modelMatrix * vec4(position, 1.0);
  vPosW = w.xyz;
  vNormalW = normalize(mat3(modelMatrix) * normal);
  gl_Position = projectionMatrix * viewMatrix * w;
}`

const BODY_FRAG = /* glsl */ `
uniform sampler2D uMap;
uniform float uHasMap;
uniform vec3 uColour;
uniform vec3 uSun;
varying vec3 vNormalW;
varying vec3 vPosW;
varying vec2 vUv;
void main() {
  vec3 base = uHasMap > 0.5 ? texture2D(uMap, vUv).rgb : uColour;
  vec3 toSun = normalize(uSun - vPosW);
  float lit = max(dot(normalize(vNormalW), toSun), 0.0);
  gl_FragColor = vec4(base * (0.07 + 0.98 * lit), 1.0);
  #include <colorspace_fragment>
}`

export function createSolarSystemScene(THREE_: typeof THREE, opts: SolarSceneOptions): SolarSceneHandle {
  const root = new THREE_.Group()
  root.name = 'solar-system'
  const disposables: Array<{ dispose(): void }> = []

  // Shared textures: a soft dot for markers, a glow for the Sun.
  const dotTexture = radialTexture(THREE_, [
    [0, 'rgba(255,255,255,1)'],
    [0.55, 'rgba(255,255,255,1)'],
    [0.75, 'rgba(255,255,255,0.35)'],
    [1, 'rgba(255,255,255,0)'],
  ])
  const glowTexture = radialTexture(THREE_, [
    [0, 'rgba(255,244,214,1)'],
    [0.2, 'rgba(255,214,120,0.55)'],
    [0.5, 'rgba(255,190,90,0.12)'],
    [1, 'rgba(255,170,60,0)'],
  ])
  disposables.push(dotTexture, glowTexture)
  const sphereGeo = new THREE_.SphereGeometry(1, 64, 32)
  disposables.push(sphereGeo)

  let layout: OrreryLayout = 'distances'
  /** 0 = distances, 1 = sizes; eased toward the target. */
  let blend = 0
  let blendTarget = 0
  let timeMs = clampEphemerisTime(Date.now())
  let orbitEpoch = Number.NaN
  let orbitBlend = Number.NaN
  let hover: SolarBodyId | null = null
  const placement = { position: new THREE_.Vector3(), quaternion: new THREE_.Quaternion(), scale: 1 }

  const nodes = new Map<SolarBodyId, BodyNode>()
  for (const body of SOLAR_BODIES) nodes.set(body.id, buildNode(body))

  // The Moon's orbit round the Earth (true sizes only): a child of the Earth's group.
  const moonOrbit = new THREE_.LineLoop(
    new THREE_.BufferGeometry().setAttribute('position', new THREE_.BufferAttribute(new Float32Array(64 * 3), 3)),
    new THREE_.LineBasicMaterial({ color: 0xc9c9c9, transparent: true, opacity: 0.5, depthWrite: false }),
  )
  moonOrbit.frustumCulled = false
  nodes.get('earth')?.group.add(moonOrbit)
  disposables.push(moonOrbit.geometry, moonOrbit.material as THREE.Material)

  // The streaks of a flight.
  const streakPos = new Float32Array(STREAKS * 2 * 3)
  const streakSeeds = new Float32Array(STREAKS * 3)
  for (let i = 0; i < STREAKS; i++) seedStreak(i, true)
  const streakGeo = new THREE_.BufferGeometry().setAttribute('position', new THREE_.BufferAttribute(streakPos, 3))
  const streakMat = new THREE_.LineBasicMaterial({ color: 0xcfe0ff, transparent: true, opacity: 0, depthWrite: false, blending: THREE_.AdditiveBlending })
  const streaks = new THREE_.LineSegments(streakGeo, streakMat)
  streaks.frustumCulled = false
  streaks.visible = false
  streaks.renderOrder = 5
  root.add(streaks)
  disposables.push(streakGeo, streakMat)

  // A flight in progress.
  let flight: null | {
    start: SolarFlightStart
    /** Heliocentric km, three axes, frozen at the start. */
    km: Map<SolarBodyId, Vec3>
    f: Vec3
    upO: Vec3
    rightO: Vec3
    kmToM: number
    startKm: number
    endKm: number
    distanceKm: number
    u: number
    lastU: number
  } = null

  // ---------------------------------------------------------------- building

  function buildNode(body: SolarBody): BodyNode {
    const group = new THREE_.Group()
    group.name = `solar-${body.id}`
    root.add(group)
    let uniforms: BodyNode['uniforms'] = null
    let material: THREE.Material
    if (body.kind === 'star') {
      material = new THREE_.MeshBasicMaterial({ color: 0xfff3cf })
    } else {
      uniforms = {
        uMap: { value: null },
        uHasMap: { value: 0 },
        uColour: { value: new THREE_.Color(body.colour) },
        uSun: { value: new THREE_.Vector3() },
      }
      material = new THREE_.ShaderMaterial({ uniforms, vertexShader: BODY_VERT, fragmentShader: BODY_FRAG })
    }
    disposables.push(material)
    const sphere = new THREE_.Mesh(sphereGeo, material)
    sphere.name = `solar-${body.id}-sphere`
    // The sphere's y axis is the body's north pole.
    const pole = toThree(equatorialToEcliptic(body.poleRaDeg, body.poleDecDeg))
    sphere.quaternion.setFromUnitVectors(new THREE_.Vector3(0, 1, 0), new THREE_.Vector3(...pole))
    group.add(sphere)

    let glow: THREE.Sprite | null = null
    if (body.kind === 'star') {
      const gm = new THREE_.SpriteMaterial({ map: glowTexture, transparent: true, depthWrite: false, blending: THREE_.AdditiveBlending })
      disposables.push(gm)
      glow = new THREE_.Sprite(gm)
      glow.renderOrder = 3
      group.add(glow)
    }

    const mm = new THREE_.SpriteMaterial({ map: dotTexture, color: body.colour, transparent: true, depthWrite: false })
    disposables.push(mm)
    const marker = new THREE_.Sprite(mm)
    marker.renderOrder = 4
    group.add(marker)

    const { sprite: label, aspect } = makeLabel(body)
    group.add(label)

    let orbit: THREE.LineLoop | null = null
    if (body.kind !== 'star' && body.id !== 'moon') {
      const geo = new THREE_.BufferGeometry().setAttribute('position', new THREE_.BufferAttribute(new Float32Array(ORBIT_POINTS * 3), 3))
      const om = new THREE_.LineBasicMaterial({ color: body.colour, transparent: true, opacity: body.kind === 'dwarf' ? 0.42 : 0.55, depthWrite: false })
      orbit = new THREE_.LineLoop(geo, om)
      orbit.frustumCulled = false
      orbit.name = `solar-${body.id}-orbit`
      root.add(orbit)
      disposables.push(geo, om)
    }

    let rings: VrPlanetRingsHandle | null = null
    if (body.id === 'saturn' || body.id === 'jupiter' || body.id === 'uranus' || body.id === 'neptune') {
      rings = createVrPlanetRings(THREE_, sphere, 1)
      rings.setBody(body.id)
    }

    return { body, group, sphere, uniforms, glow, marker, label, labelAspect: aspect, orbit, rings, radius: 0, pictureAsked: false }
  }

  function makeLabel(body: SolarBody): { sprite: THREE.Sprite; aspect: number } {
    const canvas = document.createElement('canvas')
    const text = opts.nameOf(body.id)
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D
    const font = '600 40px system-ui, -apple-system, "Segoe UI", sans-serif'
    ctx.font = font
    const w = Math.ceil(ctx.measureText(text).width) + 24
    canvas.width = w
    canvas.height = 56
    ctx.font = font
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.lineJoin = 'round'
    ctx.lineWidth = 8
    ctx.strokeStyle = 'rgba(0,0,0,0.85)'
    ctx.strokeText(text, w / 2, 29)
    ctx.fillStyle = '#ffffff'
    ctx.fillText(text, w / 2, 29)
    const tex = new THREE_.CanvasTexture(canvas)
    tex.colorSpace = THREE_.SRGBColorSpace
    tex.minFilter = THREE_.LinearFilter
    const mat = new THREE_.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, depthTest: false })
    const sprite = new THREE_.Sprite(mat)
    sprite.center.set(0.5, 0)
    sprite.renderOrder = 6
    sprite.name = `solar-${body.id}-label`
    return { sprite, aspect: w / 56 }
  }

  function loadPicture(node: BodyNode): void {
    if (node.pictureAsked || !node.uniforms || !opts.pictureUrl) return
    node.pictureAsked = true
    const url = opts.pictureUrl(node.body.id)
    if (!url) return
    const loader = new THREE_.TextureLoader()
    loader.setCrossOrigin('anonymous')
    loader.load(url, (tex) => {
      if (!node.uniforms) return tex.dispose()
      tex.colorSpace = THREE_.SRGBColorSpace
      tex.anisotropy = 4
      node.uniforms.uMap.value = tex
      node.uniforms.uHasMap.value = 1
      disposables.push(tex)
    })
  }

  // ---------------------------------------------------------------- overview

  function eased(b: number): number {
    return b * b * (3 - 2 * b)
  }

  /** Lay the bodies and orbits out for the current time and blend. */
  function placeOverview(): void {
    const e = eased(blend)
    const earth = heliocentricAu('earth', timeMs)
    const moonGeo = moonGeocentricKm(timeMs)
    const moonAu: Vec3 = [moonGeo[0] / AU_KM, moonGeo[1] / AU_KM, moonGeo[2] / AU_KM]
    for (const node of nodes.values()) {
      const id = node.body.id
      const helio = heliocentricAu(id, timeMs)
      const a = layoutBodyPoint('distances', id, helio, earth, moonAu)
      const b = layoutBodyPoint('sizes', id, helio, earth, moonAu)
      const p = toThree([a[0] + (b[0] - a[0]) * e, a[1] + (b[1] - a[1]) * e, a[2] + (b[2] - a[2]) * e])
      node.group.position.set(p[0], p[1], p[2])
      const ra = layoutRadius('distances', node.body.radiusKm)
      const rb = layoutRadius('sizes', node.body.radiusKm)
      node.radius = Math.exp(Math.log(ra) + (Math.log(rb) - Math.log(ra)) * e)
      node.sphere.scale.setScalar(node.radius)
    }
    // The Moon's orbit shows as the sizes layout comes in; at true distances it is 0.13 mm.
    const moonNode = nodes.get('moon') as BodyNode
    moonOrbit.visible = e > 0.05
    ;(moonOrbit.material as THREE.LineBasicMaterial).opacity = 0.5 * e
    if (moonOrbit.visible) {
      const pos = moonOrbit.geometry.getAttribute('position') as THREE.BufferAttribute
      const path = orbitPath('moon', timeMs, 64)
      for (let k = 0; k < 64; k++) {
        const d = Math.hypot(...path[k]) || 1
        const v = toThree([(path[k][0] / d) * SIZES_MOON_GAP_M, (path[k][1] / d) * SIZES_MOON_GAP_M, (path[k][2] / d) * SIZES_MOON_GAP_M])
        pos.setXYZ(k, v[0], v[1], v[2])
      }
      pos.needsUpdate = true
    }
    moonNode.group.visible = e > 0.02 || !!flight

    if (Math.abs(timeMs - orbitEpoch) > 365.25 * 86_400_000 || orbitBlend !== e) {
      orbitEpoch = timeMs
      orbitBlend = e
      for (const node of nodes.values()) {
        if (!node.orbit) continue
        const pos = node.orbit.geometry.getAttribute('position') as THREE.BufferAttribute
        const path = orbitPath(node.body.id, timeMs, ORBIT_POINTS)
        for (let k = 0; k < ORBIT_POINTS; k++) {
          const a = layoutPoint('distances', path[k])
          const b = layoutPoint('sizes', path[k])
          const v = toThree([a[0] + (b[0] - a[0]) * e, a[1] + (b[1] - a[1]) * e, a[2] + (b[2] - a[2]) * e])
          pos.setXYZ(k, v[0], v[1], v[2])
        }
        pos.needsUpdate = true
      }
    }
    if (blendTarget === 1 || blend > 0.01) for (const node of nodes.values()) loadPicture(node)
  }

  // ---------------------------------------------------------------- flight

  function placeFlight(): void {
    if (!flight) return
    const { km, f, kmToM } = flight
    const to = km.get(flight.start.to) as Vec3
    const r = remainingKm(flight.u, flight.startKm, flight.endKm)
    const viewer: Vec3 = [to[0] - f[0] * r, to[1] - f[1] * r, to[2] - f[2] * r]
    for (const node of nodes.values()) {
      const x = km.get(node.body.id) as Vec3
      let d: Vec3 = [(x[0] - viewer[0]) * kmToM, (x[1] - viewer[1]) * kmToM, (x[2] - viewer[2]) * kmToM]
      let radius = node.body.radiusKm * kmToM
      const dist = Math.hypot(d[0], d[1], d[2])
      if (dist > FAR_M) {
        const k = FAR_M / dist
        d = [d[0] * k, d[1] * k, d[2] * k]
        radius *= k
      }
      node.group.position.set(d[0], d[1], d[2])
      node.radius = radius
      node.sphere.scale.setScalar(radius)
      node.group.visible = true
    }
  }

  function seedStreak(i: number, anywhere: boolean): void {
    // Polar offset round the travel axis (0.4–7 m) and a place ahead (2–60 m).
    const ang = Math.random() * Math.PI * 2
    const rad = 0.4 + Math.pow(Math.random(), 0.6) * 6.6
    streakSeeds[i * 3] = Math.cos(ang) * rad
    streakSeeds[i * 3 + 1] = Math.sin(ang) * rad
    streakSeeds[i * 3 + 2] = anywhere ? 2 + Math.random() * 58 : 40 + Math.random() * 20
  }

  function placeStreaks(dt: number): void {
    if (!flight) return
    const strength = streakStrength(flight.u)
    streaks.visible = strength > 0.01
    streakMat.opacity = Math.min(0.9, strength * 1.1)
    if (!streaks.visible) return
    const { f, upO, rightO } = flight
    // Up to 220 m/s past the viewer at the flight's height; a streak is as long as 0.12 s of travel.
    const speed = 220 * strength
    const len = Math.max(0.05, speed * 0.12)
    for (let i = 0; i < STREAKS; i++) {
      streakSeeds[i * 3 + 2] -= speed * dt
      if (streakSeeds[i * 3 + 2] < -2) seedStreak(i, false)
      const a = streakSeeds[i * 3]
      const b = streakSeeds[i * 3 + 1]
      const t = streakSeeds[i * 3 + 2]
      for (let k = 0; k < 2; k++) {
        const tt = k === 0 ? t : t + len
        const o = (i * 2 + k) * 3
        streakPos[o] = rightO[0] * a + upO[0] * b + f[0] * tt
        streakPos[o + 1] = rightO[1] * a + upO[1] * b + f[1] * tt
        streakPos[o + 2] = rightO[2] * a + upO[2] * b + f[2] * tt
      }
    }
    ;(streakGeo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true
  }

  // ---------------------------------------------------------------- per frame

  const tmpV = new THREE_.Vector3()
  const tmpW = new THREE_.Vector3()
  const tmpS = new THREE_.Vector3()

  function rootScale(): number {
    root.getWorldScale(tmpS)
    return tmpS.x
  }

  function update(cameraWorld: THREE.Vector3, ppr: number, dt: number): void {
    if (!flight && blend !== blendTarget) {
      const step = dt / LAYOUT_SECONDS
      blend = blendTarget > blend ? Math.min(blendTarget, blend + step) : Math.max(blendTarget, blend - step)
      placeOverview()
    }
    if (flight) placeStreaks(dt)
    root.updateMatrixWorld(true)
    const sunNode = nodes.get('sun') as BodyNode
    sunNode.group.getWorldPosition(tmpW)
    const sunWorld = tmpW.clone()
    const scale = rootScale()
    const placedDirs: THREE.Vector3[] = []
    // A label is several times wider than tall: two closer than about three label-heights would overlap side by side.
    const labelAngle = (LABEL_PX * 3) / ppr
    // Bigger bodies get their labels first.
    const order = [...nodes.values()].sort((a, b) => b.body.radiusKm - a.body.radiusKm)
    for (const node of order) {
      if (!node.group.visible) continue
      node.group.getWorldPosition(tmpV)
      const dist = Math.max(1e-6, tmpV.distanceTo(cameraWorld))
      const worldRadius = node.radius * scale
      const markerPx = MARKER_PX[node.body.kind] * (hover === node.body.id ? 1.5 : 1)
      const markerWorld = (dist * markerPx) / ppr
      const discPx = (worldRadius / dist) * ppr
      // In the group's units (the root's scale divides out).
      node.marker.scale.setScalar(markerWorld / scale)
      node.marker.visible = discPx * 2 < markerPx * 1.1
      if (node.glow) node.glow.scale.setScalar(Math.max(worldRadius * 6, (dist * 70) / ppr) / scale)
      if (node.uniforms) node.uniforms.uSun.value.copy(sunWorld)
      // The label, if it does not land on a bigger body's.
      const dir = tmpV.clone().sub(cameraWorld).normalize()
      const inFlight = !!flight
      const wanted = !inFlight || node.body.id === flight?.start.to || node.body.id === flight?.start.from || node.body.kind === 'star'
      let free = wanted && placedDirs.every((d) => d.angleTo(dir) > labelAngle)
      if (hover === node.body.id) free = true
      node.label.visible = free
      if (free) {
        placedDirs.push(dir)
        const h = (dist * LABEL_PX) / ppr / scale
        node.label.scale.set(h * node.labelAspect * (56 / 40), h * (56 / 40), 1)
        const lift = Math.max(node.radius, markerWorld / scale / 2) + (dist * 3) / ppr / scale
        // camUp is in world; the group's local axes are the root's, so the lift is along local +y in the overview.
        node.label.position.set(0, lift, 0)
        if (inFlight) node.label.position.copy(tmpV.set(0, 1, 0).applyQuaternion(root.getWorldQuaternion(new THREE_.Quaternion()).invert()).multiplyScalar(lift))
      }
    }
  }

  // ---------------------------------------------------------------- public

  const handle: SolarSceneHandle = {
    root,
    get layout() {
      return layout
    },
    get timeMs() {
      return timeMs
    },
    get flying() {
      return !!flight
    },

    setLayout(next, animate = true) {
      layout = next
      blendTarget = next === 'sizes' ? 1 : 0
      if (!animate) {
        blend = blendTarget
        placeOverview()
      }
    },

    setTime(ms) {
      timeMs = clampEphemerisTime(ms)
      if (!flight) placeOverview()
    },

    setPlacement(position, quaternion, scale) {
      placement.position.copy(position)
      placement.quaternion.copy(quaternion)
      placement.scale = scale
      if (!flight) {
        root.position.copy(position)
        root.quaternion.copy(quaternion)
        root.scale.setScalar(scale)
      }
    },

    beginFlight(start) {
      const km = new Map<SolarBodyId, Vec3>()
      for (const b of SOLAR_BODIES) {
        const h = heliocentricAu(b.id, start.timeMs)
        km.set(b.id, toThree([h[0] * AU_KM, h[1] * AU_KM, h[2] * AU_KM]))
      }
      const a = km.get(start.from) as Vec3
      const b = km.get(start.to) as Vec3
      const dv: Vec3 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]]
      const distanceKm = Math.hypot(...dv)
      const f: Vec3 = [dv[0] / distanceKm, dv[1] / distanceKm, dv[2] / distanceKm]
      // Local up: the arriving body's pole, square to the travel; the ecliptic's north if the two line up.
      const toBody = nodes.get(start.to) as BodyNode
      const pole = new THREE_.Vector3(0, 1, 0).applyQuaternion(toBody.sphere.quaternion)
      let up = new THREE_.Vector3(pole.x, pole.y, pole.z)
      const fv = new THREE_.Vector3(...f)
      up.sub(fv.clone().multiplyScalar(up.dot(fv)))
      if (up.lengthSq() < 1e-6) {
        up = new THREE_.Vector3(0, 1, 0).sub(fv.clone().multiplyScalar(fv.y))
        if (up.lengthSq() < 1e-6) up.set(1, 0, 0)
      }
      up.normalize()
      const right = fv.clone().cross(up).normalize()
      // World basis: ahead, up square to it, right.
      const aheadW = start.ahead.clone().normalize()
      const upW = start.up.clone().sub(aheadW.clone().multiplyScalar(start.up.dot(aheadW))).normalize()
      const rightW = aheadW.clone().cross(upW).normalize()
      const w = new THREE_.Matrix4().makeBasis(rightW, upW, aheadW)
      const o = new THREE_.Matrix4().makeBasis(right, up, fv)
      const rot = w.multiply(o.transpose())
      root.position.copy(start.viewer)
      root.quaternion.setFromRotationMatrix(rot)
      root.scale.setScalar(1)

      const kmToM = start.arrivalRadiusM / toBody.body.radiusKm
      const fromBody = nodes.get(start.from) as BodyNode
      const endKm = start.arrivalDistanceM / kmToM
      const departKm = fromBody.body.radiusKm * (start.arrivalDistanceM / start.arrivalRadiusM)
      const startKm = Math.max(endKm * 1.01, distanceKm - departKm)
      flight = { start, km, f, upO: [up.x, up.y, up.z], rightO: [right.x, right.y, right.z], kmToM, startKm, endKm, distanceKm, u: 0, lastU: 0 }
      for (const node of nodes.values()) if (node.orbit) node.orbit.visible = false
      moonOrbit.visible = false
      for (let i = 0; i < STREAKS; i++) seedStreak(i, true)
      loadPicture(toBody)
      loadPicture(fromBody)
      placeFlight()
      return distanceKm
    },

    setFlightProgress(u) {
      if (!flight) return
      flight.u = Math.min(1, Math.max(0, u))
      placeFlight()
    },

    endFlight() {
      if (!flight) return
      flight = null
      streaks.visible = false
      root.position.copy(placement.position)
      root.quaternion.copy(placement.quaternion)
      root.scale.setScalar(placement.scale)
      for (const node of nodes.values()) {
        if (node.orbit) node.orbit.visible = true
        node.group.visible = true
        node.label.position.set(0, 0, 0)
      }
      orbitBlend = Number.NaN
      placeOverview()
    },

    update,

    pick(origin, dir, toleranceRad) {
      if (flight) return null
      root.updateMatrixWorld(true)
      const scale = rootScale()
      let best: SolarBodyId | null = null
      let bestAngle = Infinity
      for (const node of nodes.values()) {
        if (!node.group.visible) continue
        node.group.getWorldPosition(tmpV)
        const to = tmpV.sub(origin)
        const dist = to.length()
        if (dist <= 0) continue
        const angle = to.angleTo(dir)
        const disc = Math.asin(Math.min(1, (node.radius * scale) / dist))
        if (angle > Math.max(toleranceRad, disc)) continue
        // Inside a disc counts as a hit at the centre, so a big body under the ray beats a marker beside it.
        const score = angle <= disc ? 0 : angle
        if (score < bestAngle || (score === bestAngle && best && node.body.radiusKm < (nodes.get(best) as BodyNode).body.radiusKm)) {
          best = node.body.id
          bestAngle = score
        }
      }
      return best
    },

    setHover(id) {
      hover = id
    },

    bodyWorldPosition(id, out) {
      root.updateMatrixWorld(true)
      return (nodes.get(id) as BodyNode).group.getWorldPosition(out)
    },

    bodyWorldRadius(id) {
      return (nodes.get(id) as BodyNode).radius * rootScale()
    },

    celestialToWorld() {
      // Celestial (equatorial J2000) → ecliptic → three's axes, then the root's turn.
      const ce = Math.cos(OBLIQUITY_J2000)
      const se = Math.sin(OBLIQUITY_J2000)
      const local = [1, 0, 0, 0, -se, ce, 0, -ce, -se]
      root.updateMatrixWorld(true)
      const e = new THREE_.Matrix4().extractRotation(root.matrixWorld).elements
      const r = [e[0], e[4], e[8], e[1], e[5], e[9], e[2], e[6], e[10]]
      const out: number[] = []
      for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) out.push(r[i * 3] * local[j] + r[i * 3 + 1] * local[3 + j] + r[i * 3 + 2] * local[6 + j])
      return out
    },

    relabel() {
      for (const node of nodes.values()) {
        node.group.remove(node.label)
        node.label.material.map?.dispose()
        node.label.material.dispose()
        const { sprite, aspect } = makeLabel(node.body)
        node.label = sprite
        node.labelAspect = aspect
        node.group.add(sprite)
      }
    },

    dispose() {
      for (const node of nodes.values()) {
        node.rings?.dispose()
        node.label.material.map?.dispose()
        node.label.material.dispose()
      }
      for (const d of disposables) d.dispose()
      root.removeFromParent()
    },
  }

  placeOverview()
  return handle
}

function radialTexture(THREE_: typeof THREE, stops: Array<[number, string]>): THREE.Texture {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = 128
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64)
  for (const [at, colour] of stops) g.addColorStop(at, colour)
  ctx.fillStyle = g
  ctx.fillRect(0, 0, 128, 128)
  const tex = new THREE_.CanvasTexture(canvas)
  tex.colorSpace = THREE_.SRGBColorSpace
  return tex
}
